import React, { useEffect } from "react";
import styles from "./EventSummaryModal.module.scss";
import StatSection from "@src/pages/home/children/pricing/components/inventory/components/EventSummaryModal/StatSection";
import { IEventSummaryData } from "@src/store/pricing/getEventSummary";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { formatCurrency } from "@src/components/utils/numberUtils";
import { formatDate } from "@src/components/utils/dateUtils";
import { TimeIcon } from "@src/assets/general-icons/TimeIcon";
import { LocationIcon } from "@src/assets/general-icons/LocationIcon";
import { pricingActions } from "@src/store";
import { Loader } from "@src/components/loader/Loader";
import { MyTooltip } from "@src/components/tooltip/Tooltip";

type TEventSummaryModal = {
	eventSummary: IEventSummaryData | null;
	isLoading: boolean;
};

const EventSummaryModal: React.FC<TEventSummaryModal> = ({ eventSummary, isLoading }) => {
	const dispatch = useAppDispatch();

	const event = useAppSelector((state) => state.pricing.selection.event);
	const { Event_ID: EventId, Event_Name, Event_Date, Venue_Name } = event ?? {};
	const CompanyName = useAppSelector((state) => state.settings.companyName);

	useEffect(() => {
		dispatch(pricingActions.fetchEventSummary({ EventId, CompanyName }));
	}, [EventId, CompanyName, dispatch]);

	if (!eventSummary || isLoading) {
		return (
			<div className={styles.root} style={{ alignItems: "center", justifyContent: "center" }}>
				<Loader />
			</div>
		);
	}

	return (
		<div className={styles.root}>
			<MyTooltip
				id="summaryHeadliner"
				popupStyle={{ fontSize: "1rem", fontWeight: 600 }}
				popup={Event_Name}
				element={<div className={styles.headliner}>{Event_Name}</div>}
			/>

			<div className={styles.bottomInfo}>
				<div className={styles.time}>
					<TimeIcon />
					{formatDate(Event_Date)} at {event?.Event_Time}
				</div>
				<div className={styles.location}>
					<LocationIcon />
					{Venue_Name}
				</div>
			</div>

			<div className={styles.summary}>
				<StatSection
					title="Total"
					ticketCount={eventSummary?.Total.TotalTickets ?? 0}
					data={[
						{
							label: "Cost",
							value: formatCurrency(eventSummary?.Total.TotalCost ?? 0, eventSummary?.Total.TotalCost !== null),
						},
						{
							label: "Expected Revenue",
							value: formatCurrency(
								eventSummary?.Total.TotalExpectedRevenue ?? 0,
								eventSummary?.Total.TotalExpectedRevenue !== null,
							),
							variant:
								eventSummary?.Total.TotalExpectedRevenue !== null && eventSummary.Total.TotalExpectedRevenue < 0
									? "negative"
									: "positive",
						},
						{
							label: "Expected Net",
							value: formatCurrency(
								eventSummary?.Total.TotalExpectedNet ?? 0,
								eventSummary?.Total.TotalExpectedNet !== null,
							),
							variant:
								eventSummary?.Total.TotalExpectedNet !== null && eventSummary.Total.TotalExpectedNet < 0
									? "negative"
									: "positive",
						},
						{
							label: "Expected Margin",
							value:
								eventSummary?.Total.TotalExpectedMargin !== null
									? `${(eventSummary.Total.TotalExpectedMargin * 100).toFixed(2)}%`
									: "-",
							variant:
								eventSummary?.Total.TotalExpectedMargin !== null && eventSummary.Total.TotalExpectedMargin < 0
									? "negative"
									: "positive",
						},
					]}
				/>

				<div className={styles.separator} />

				<StatSection
					title="Sold"
					ticketCount={eventSummary?.Sold.SoldTickets}
					data={[
						{
							label: "Cost",
							value: formatCurrency(eventSummary?.Sold.SoldCost ?? 0, eventSummary?.Sold.SoldCost !== null),
						},
						{
							label: "Commission",
							value: formatCurrency(eventSummary?.Sold.SoldCommission ?? 0, eventSummary?.Sold.SoldCommission !== null),
						},
						{
							label: "Net",
							value: formatCurrency(eventSummary?.Sold.SoldPayment ?? 0, eventSummary?.Sold.SoldPayment !== null),
						},
						{
							label: "Profit",
							value: formatCurrency(eventSummary?.Sold.SoldProfit ?? 0, eventSummary?.Sold.SoldProfit !== null),
							variant:
								eventSummary?.Sold.SoldProfit !== null && eventSummary.Sold.SoldProfit < 0 ? "negative" : "positive",
						},
						{
							label: "Profit Margin",
							value:
								eventSummary?.Sold.SoldProfitMargin !== null
									? `${(eventSummary.Sold.SoldProfitMargin * 100).toFixed(2)}%`
									: "-",
							variant:
								eventSummary?.Sold.SoldProfitMargin !== null && eventSummary.Sold.SoldProfitMargin < 0
									? "negative"
									: "positive",
						},
					]}
				/>

				<div className={styles.separator} />

				<StatSection
					title="Open"
					ticketCount={eventSummary?.Open.OpenTickets}
					data={[
						{
							label: "Cost",
							value: formatCurrency(eventSummary?.Open.OpenCost ?? 0, eventSummary?.Open.OpenCost !== null),
						},
						{
							label: "Price",
							value: formatCurrency(eventSummary?.Open.OpenPrice ?? 0, eventSummary?.Open.OpenPrice !== null),
						},
						{
							label: "Position",
							value: formatCurrency(eventSummary?.Open.OpenPosition ?? 0, eventSummary?.Open.OpenPosition !== null),
							variant:
								eventSummary?.Open.OpenPosition !== null && eventSummary.Open.OpenPosition < 0 ? "negative" : "positive",
						},
						{
							label: "Margin",
							value: eventSummary?.Open.OpenMargin !== null ? `${(eventSummary.Open.OpenMargin * 100).toFixed(2)}%` : "-",
							variant:
								eventSummary?.Open.OpenMargin !== null && eventSummary.Open.OpenMargin < 0 ? "negative" : "positive",
						},
					]}
				/>
			</div>
		</div>
	);
};

export default EventSummaryModal;
